<template>
  <div class="tab-container">
    <div class="tabs">
      <div class="tabs-wrapper">
        <button
          v-for="tab in filteredTabs"
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="activeTab = tab.id"
        >
          <span class="material-symbols-outlined">{{ tab.icon }}</span>
          <span class="tab-label">{{ tab.label }}</span>
        </button>
      </div>
    </div>
    <div class="tab-content">
      <overview-tab
        v-if="activeTab === 'overview'"
        :app="appData.app"
        :developer="appData.developer"
      />
      <description-tab v-if="activeTab === 'description'" :app="appData.app" />
      <screenshots-tab
        v-if="activeTab === 'screenshots'"
        :screenshots="appData.app.appScreenshotsElements"
      />
      <developer-tab
        v-if="activeTab === 'developer'"
        :developer="appData.developer"
      />
      <additional-info-tab
        v-if="activeTab === 'additional'"
        :app="appData.app"
      />

      <history-tab v-if="activeTab === 'history' && isHistory" :historyData="historyData" :historyId="historyId"/>
    </div>
  </div>
</template>

<script>
import OverviewTab from "./OverviewTab.vue";
import DescriptionTab from "./DescriptionTab.vue";
import ScreenshotsTab from "./ScreenshotsTab.vue";
import DeveloperTab from "./DeveloperTab.vue";
import AdditionalInfoTab from "./AdditionalInfoTab.vue";
import HistoryTab from "./HistoryTab.vue";

export default {
  name: "TabView",
  components: {
    OverviewTab,
    DescriptionTab,
    ScreenshotsTab,
    DeveloperTab,
    AdditionalInfoTab,
    HistoryTab,
  },
  props: {
    appData: {
      type: Object,
      required: true,
    },
    historyData: {
      type: Array,
      required: false,
      default: null
    },
    isHistory: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultTab: {
      type: String,
      required: false,
      default: "overview",
    },
    historyId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      activeTab: this.defaultTab,
      tabs: [
        { id: "overview", label: this.$t('Apps.overview'), icon: "home" },
        { id: "description", label: this.$t('Apps.description'), icon: "description" },
        { id: "screenshots", label: this.$t('Apps.screenshots'), icon: "photo_library" },
        { id: "developer", label: this.$t('Apps.developer'), icon: "developer_mode" },
        { id: "additional", label: this.$t('Apps.additionalInformation'), icon: "info" },
        { id: "history", label: this.$t('Apps.history'), icon: "history" },
      ],
    };
  },
  computed: {
    filteredTabs() {
      return this.tabs.filter((tab) => {
        if (tab.id === "history") {
          return this.isHistory;
        }
        return true;
      });
    },
  },
  watch: {
    defaultTab(newTab) {
      if (this.tabs.some((tab) => tab.id === newTab)) {
        this.activeTab = newTab; 
      }
    },
  },
};
</script>

<style scoped>
.tab-container {
  width: 100%;
  /* height: calc(100vh - 120px); */
}

.tabs {
  display: flex;
  justify-content: center;
  border-bottom: 1px solid #ddd;
  background: #fff;
  border-radius: 0px 0px 10px 10px;
  padding: 0 20px;
  box-shadow: 0px 1px 5px 0px #9e9e9e;
}

.tabs-wrapper {
  display: flex;
  max-width: 1200px;
  width: 100%;
  justify-content: space-between;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.tab-button {
  padding: 15px 25px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex: 1;
  justify-content: center;
}

.tab-button .material-symbols-outlined {
  font-size: 20px;
}

.tab-button.active {
  color: var(--logo-color);
  border-bottom: 2px solid var(--logo-color);
}

.tab-button:hover {
  background-color: var(--hover-color);
  color: var(--logo-color);
}

.tab-content {
  height: calc(100% - 52px);
  overflow-y: auto;
  padding: 20px;
  max-width: 1200px;
  margin: 4px auto;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .tab-container {
    height: calc(100vh - 100px);
  }

  .tabs {
    padding: 0 8px;
  }

  .tab-button {
    padding: 8px 24px;
    flex-direction: column;
    gap: 4px;
  }

  .tabs-wrapper {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .tabs-wrapper::-webkit-scrollbar {
    display: none;
  }

  .tab-button .material-symbols-outlined {
    font-size: 24px;
  }

  .tab-label {
    font-size: 12px;
    white-space: nowrap;
  }

  .tab-content {
    padding: 12px 5px;
  }
}

/* Small Mobile Devices */
@media (max-width: 480px) {
  .tab-container {
    height: calc(100vh - 80px);
  }

  .tabs {
    padding: 0 4px;
  }

  .tab-button {
    padding: 8px 4px;
  }

  .tab-button .material-symbols-outlined {
    font-size: 20px;
  }

  .tab-label {
    font-size: 11px;
  }
}

/* Landscape Mode */
@media (max-height: 480px) and (orientation: landscape) {
  .tab-container {
    height: calc(100vh - 60px);
  }

  .tabs {
    padding: 0;
    overflow: auto;
  }

  .tab-button {
    flex-direction: row;
    padding: 8px;
  }

  .tab-label {
    display: none;
  }
}
</style>
